/**
 * Manager Calendar - Deadlines + Google Calendar
 * Shows compliance deadlines, Google Calendar events, and voice-command event creation.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Clock, AlertTriangle, CheckCircle, Loader2, Calendar, Plus, X, Check, Link2, ExternalLink, CalendarPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

interface PendingEvent {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  description?: string;
}

export default function CalendarPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'deadlines' | 'calendar'>(searchParams.get('calendar_connected') ? 'calendar' : 'deadlines');
  const [showCreate, setShowCreate] = useState(false);
  const [pendingEvent, setPendingEvent] = useState<PendingEvent | null>(null);
  const [newEvent, setNewEvent] = useState<PendingEvent>({ title: '', date: '', startTime: '09:00', endTime: '10:00', description: '' });

  // Pick up pending event from voice command
  useEffect(() => {
    const stored = sessionStorage.getItem('pa-pending-calendar-event');
    if (stored) {
      sessionStorage.removeItem('pa-pending-calendar-event');
      try {
        const ev = JSON.parse(stored);
        setPendingEvent(ev);
        setShowCreate(true);
        setActiveTab('calendar');
      } catch {}
    }
  }, []);

  // Fetch deadlines
  const { data: deadlinesData, isLoading: loadingDeadlines } = useQuery({
    queryKey: ['manager-deadlines'],
    queryFn: async () => { const r = await apiClient.get('/deadlines'); return r.data.data; },
  });

  // Check Google Calendar connection
  const { data: calStatus } = useQuery({
    queryKey: ['google-calendar-status'],
    queryFn: async () => { const r = await apiClient.get('/calendar/google/status'); return r.data.data; },
    retry: false,
  });

  const isConnected = calStatus?.connected === true;

  // Fetch Google Calendar events (next 30 days)
  const { data: calEvents, isLoading: loadingEvents } = useQuery({
    queryKey: ['google-calendar-events'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const future = new Date(Date.now() + 30 * 86400000).toISOString();
      const r = await apiClient.get(`/calendar/google/events?timeMin=${now}&timeMax=${future}&maxResults=50`);
      return r.data.data?.events || [];
    },
    enabled: isConnected,
  });

  // Create event mutation
  const createMutation = useMutation({
    mutationFn: async (ev: PendingEvent) => {
      const startTime = `${ev.date}T${ev.startTime}:00`;
      const endTime = `${ev.date}T${ev.endTime}:00`;
      return apiClient.post('/calendar/google/events', {
        title: ev.title,
        startTime,
        endTime,
        description: ev.description || '',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-calendar-events'] });
      setPendingEvent(null);
      setShowCreate(false);
      setNewEvent({ title: '', date: '', startTime: '09:00', endTime: '10:00', description: '' });
    },
  });

  // Connect to Google Calendar
  const handleConnect = async () => {
    try {
      const r = await apiClient.get('/calendar/google/connect?returnUrl=/pa/calendar');
      if (r.data?.data?.authUrl) window.location.href = r.data.data.authUrl;
    } catch {}
  };

  // Parse voice command into event
  const parseVoiceEvent = (text: string): PendingEvent | null => {
    const today = new Date();
    let date = today.toISOString().split('T')[0];
    let startTime = '09:00';
    let endTime = '10:00';

    // Parse "tomorrow"
    if (/tomorrow/i.test(text)) {
      const d = new Date(today); d.setDate(d.getDate() + 1);
      date = d.toISOString().split('T')[0];
    }
    // Parse "next monday/tuesday/etc"
    const dayMatch = text.match(/next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
    if (dayMatch) {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const target = days.indexOf(dayMatch[1].toLowerCase());
      const d = new Date(today);
      const diff = (target - d.getDay() + 7) % 7 || 7;
      d.setDate(d.getDate() + diff);
      date = d.toISOString().split('T')[0];
    }
    // Parse time like "at 2pm", "at 14:00", "at 3:30pm"
    const timeMatch = text.match(/at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (timeMatch) {
      let h = parseInt(timeMatch[1]);
      const m = timeMatch[2] || '00';
      if (timeMatch[3]?.toLowerCase() === 'pm' && h < 12) h += 12;
      if (timeMatch[3]?.toLowerCase() === 'am' && h === 12) h = 0;
      startTime = `${h.toString().padStart(2, '0')}:${m}`;
      const endH = h + 1;
      endTime = `${endH.toString().padStart(2, '0')}:${m}`;
    }

    // Remove date/time parts to get the title
    let title = text
      .replace(/tomorrow/i, '')
      .replace(/next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i, '')
      .replace(/at\s+\d{1,2}(?::\d{2})?\s*(?:am|pm)?/i, '')
      .replace(/schedule\s*/i, '')
      .replace(/add\s*/i, '')
      .replace(/create\s*(?:an?\s*)?(?:event|meeting|appointment)?\s*/i, '')
      .replace(/book\s*/i, '')
      .trim();

    if (!title) return null;
    title = title.charAt(0).toUpperCase() + title.slice(1);

    return { title, date, startTime, endTime };
  };

  // Listen for voice commands from PA input
  // The PA layout dispatches 'pa-query' events; we intercept calendar-related ones
  // This is handled in the layout; here we expose parseVoiceEvent for potential use

  const deadlines = deadlinesData?.deadlines || [];
  const grouped: Record<string, any[]> = {};
  for (const d of deadlines) {
    const date = d.dueDate?.split('T')[0] || 'unknown';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(d);
  }
  const sortedDates = Object.keys(grouped).sort();
  const today = new Date().toISOString().split('T')[0];

  // Group calendar events by date
  const eventsByDate: Record<string, any[]> = {};
  for (const ev of (calEvents || [])) {
    const date = (ev.start?.dateTime || ev.start?.date || '').split('T')[0];
    if (!eventsByDate[date]) eventsByDate[date] = [];
    eventsByDate[date].push(ev);
  }
  const eventDates = Object.keys(eventsByDate).sort();

  const isLoading = activeTab === 'deadlines' ? loadingDeadlines : loadingEvents;

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="h-5 w-5 text-[var(--v4-accent)]" />
        <h2 className="text-lg font-medium text-foreground">Calendar</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1">
        <button
          onClick={() => setActiveTab('deadlines')}
          className={cn('flex-1 text-xs py-2 rounded-lg font-medium transition', activeTab === 'deadlines' ? 'bg-[var(--v4-accent)] text-white' : 'text-muted-foreground')}
        >
          Deadlines ({deadlines.length})
        </button>
        <button
          onClick={() => setActiveTab('calendar')}
          className={cn('flex-1 text-xs py-2 rounded-lg font-medium transition', activeTab === 'calendar' ? 'bg-[var(--v4-accent)] text-white' : 'text-muted-foreground')}
        >
          Google Calendar
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-[40vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
        </div>
      )}

      {/* Deadlines Tab */}
      {activeTab === 'deadlines' && !isLoading && (
        sortedDates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No upcoming deadlines</p>
          </div>
        ) : (
          sortedDates.map(date => {
            const items = grouped[date];
            const isOverdue = date < today;
            const isToday = date === today;
            const dateObj = new Date(date + 'T00:00:00');
            const daysAway = Math.ceil((dateObj.getTime() - Date.now()) / 86400000);

            return (
              <div key={date}>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-background py-1 z-10">
                  <span className={cn('text-xs font-medium', isOverdue ? 'text-red-600 dark:text-red-400' : isToday ? 'text-[var(--v4-accent)]' : 'text-muted-foreground')}>
                    {isToday ? 'Today' : dateObj.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {isOverdue && <span className="text-xs text-red-600 dark:text-red-400 font-medium">{Math.abs(daysAway)}d overdue</span>}
                  {!isOverdue && !isToday && <span className="text-sm text-muted-foreground">{daysAway}d</span>}
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-2 ml-2">
                  {items.map((d: any, i: number) => (
                    <div key={i} className={cn(
                      'flex items-start gap-3 p-3 rounded-xl border transition',
                      isOverdue ? 'bg-red-500/10 border-red-500/20' : isToday ? 'bg-[var(--v4-accent)]/10 border-[var(--v4-accent)]/20' : daysAway <= 7 ? 'bg-amber-500/10 border-amber-500/10' : 'bg-muted/50 border-border'
                    )}>
                      {isOverdue ? <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" /> : <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-sm font-medium text-foreground">{d.clientName || 'All Clients'}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2 break-words">{d.title || d.type || d.description?.split('.')[0]}</p>
                        {isOverdue && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Due {dateObj.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} ({Math.abs(daysAway)} days overdue)</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )
      )}

      {/* Google Calendar Tab */}
      {activeTab === 'calendar' && !isLoading && (
        !isConnected ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-[var(--v4-accent)]/10 flex items-center justify-center mx-auto mb-4">
              <Link2 className="h-7 w-7 text-[var(--v4-accent)]" />
            </div>
            <h3 className="text-base font-medium mb-2 text-foreground">Connect Google Calendar</h3>
            <p className="text-sm text-muted-foreground mb-6 px-4">View events, add meetings via voice, and sync deadlines with your calendar.</p>
            <button onClick={handleConnect} className="px-6 py-2.5 bg-[var(--v4-accent)] text-white rounded-xl text-sm font-medium hover:bg-[var(--v4-accent-hover)] transition active:scale-95">
              Connect Google Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add Event Button */}
            <button
              onClick={() => setShowCreate(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-[var(--v4-accent)]/30 text-[var(--v4-accent)] text-sm hover:bg-[var(--v4-accent)]/10 transition"
            >
              <Plus className="h-4 w-4" /> Add Event
            </button>

            {/* Event Creation Form */}
            {showCreate && (
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">New Event</span>
                  <button onClick={() => { setShowCreate(false); setPendingEvent(null); }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                </div>
                <input
                  placeholder="Event title"
                  value={pendingEvent?.title ?? newEvent.title}
                  onChange={e => pendingEvent ? setPendingEvent({ ...pendingEvent, title: e.target.value }) : setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-[var(--v4-accent)]/40"
                />
                <input
                  type="date"
                  value={pendingEvent?.date ?? newEvent.date}
                  onChange={e => pendingEvent ? setPendingEvent({ ...pendingEvent, date: e.target.value }) : setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--v4-accent)]/40"
                />
                <div className="flex gap-2">
                  <input
                    type="time"
                    value={pendingEvent?.startTime ?? newEvent.startTime}
                    onChange={e => pendingEvent ? setPendingEvent({ ...pendingEvent, startTime: e.target.value }) : setNewEvent({ ...newEvent, startTime: e.target.value })}
                    className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--v4-accent)]/40"
                  />
                  <input
                    type="time"
                    value={pendingEvent?.endTime ?? newEvent.endTime}
                    onChange={e => pendingEvent ? setPendingEvent({ ...pendingEvent, endTime: e.target.value }) : setNewEvent({ ...newEvent, endTime: e.target.value })}
                    className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--v4-accent)]/40"
                  />
                </div>
                <input
                  placeholder="Description (optional)"
                  value={pendingEvent?.description ?? newEvent.description}
                  onChange={e => pendingEvent ? setPendingEvent({ ...pendingEvent, description: e.target.value }) : setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-[var(--v4-accent)]/40"
                />
                <button
                  onClick={() => {
                    const ev = pendingEvent || newEvent;
                    if (ev.title && ev.date) createMutation.mutate(ev);
                  }}
                  disabled={createMutation.isPending || !(pendingEvent?.title || newEvent.title) || !(pendingEvent?.date || newEvent.date)}
                  className="w-full py-2.5 bg-[var(--v4-accent)] text-white rounded-lg text-sm font-medium hover:bg-[var(--v4-accent-hover)] transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
                  {pendingEvent ? 'Confirm & Add to Calendar' : 'Add to Calendar'}
                </button>
                {createMutation.isSuccess && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="h-3 w-3" /> Event added to Google Calendar</p>
                )}
                {createMutation.isError && (
                  <p className="text-xs text-red-600 dark:text-red-400">Failed to create event. Try again.</p>
                )}
              </div>
            )}

            {/* Voice Command Approval Card */}
            {pendingEvent && !showCreate && (
              <div className="bg-[var(--v4-accent)]/10 border border-[var(--v4-accent)]/20 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium flex items-center gap-2 text-foreground">
                  <CalendarPlus className="h-4 w-4 text-[var(--v4-accent)]" /> Add this event?
                </p>
                <div className="text-sm space-y-1 text-foreground">
                  <p><span className="text-muted-foreground">Title:</span> {pendingEvent.title}</p>
                  <p><span className="text-muted-foreground">Date:</span> {pendingEvent.date}</p>
                  <p><span className="text-muted-foreground">Time:</span> {pendingEvent.startTime} - {pendingEvent.endTime}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => createMutation.mutate(pendingEvent)}
                    disabled={createMutation.isPending}
                    className="flex-1 py-2 bg-[var(--v4-accent)] text-white rounded-lg text-sm font-medium hover:bg-[var(--v4-accent-hover)] transition flex items-center justify-center gap-1"
                  >
                    {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Confirm
                  </button>
                  <button onClick={() => setPendingEvent(null)} className="flex-1 py-2 bg-muted rounded-lg text-sm text-muted-foreground hover:bg-muted/70 transition flex items-center justify-center gap-1">
                    <X className="h-3.5 w-3.5" /> Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Events List */}
            {eventDates.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No upcoming events</p>
              </div>
            ) : (
              eventDates.map(date => {
                const events = eventsByDate[date];
                const dateObj = new Date(date + 'T00:00:00');
                const isToday = date === today;

                return (
                  <div key={date}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cn('text-xs font-medium', isToday ? 'text-[var(--v4-accent)]' : 'text-muted-foreground')}>
                        {isToday ? 'Today' : dateObj.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="space-y-2 ml-2">
                      {events.map((ev: any) => {
                        const start = ev.start?.dateTime ? new Date(ev.start.dateTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : 'All day';
                        const end = ev.end?.dateTime ? new Date(ev.end.dateTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={ev.id} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                            <div className="w-1 h-8 rounded-full bg-[var(--v4-accent)] shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground">{ev.summary || 'Untitled'}</p>
                              <p className="text-xs text-muted-foreground">{start}{end ? ` - ${end}` : ''}</p>
                              {ev.location && <p className="text-xs text-muted-foreground mt-0.5">{ev.location}</p>}
                            </div>
                            {ev.htmlLink && (
                              <a href={ev.htmlLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )
      )}
    </div>
  );
}
